import React, { useState } from "react";
import validateJwt from "../../api/ValidateJwt";
import ReactGA from "react-ga4";
import awsconfig from "../../aws-exports";
import Cookies from "js-cookie";
import style from "./HoldingPage.module.css";
import {AuthState} from "@aws-amplify/ui-components";
import Routes from "../../config/api";

interface IProps {
    token: string | null;
    setAuthState: any;
}

/**
 * The holding page component is responsible for dealing with the first touchpoint
 * for the auto-login process via a shared token or SSO. It's really used as a kind of
 * splash screen so the user doesn't see an empty screen.
 */
export default function HoldingPage({ token, setAuthState}: IProps) {
    const [valid, setValid] = useState(true);

    React.useEffect(() => {
        async function checkAuth() {
            if (token) {
                const authData: any = await validateJwt(token);
                if (!authData.success) {
                    // The token they provided wasn't valid, so we will just redirect
                    // them to the main homepage for now.
                    // window.location.href = '/';
                    setValid(false);
                    ReactGA.event({
                        category: "Login As",
                        action: "failed",
                        label: "invalid token",
                        value: 0
                    });
                } else {
                    localStorage.setItem(
                        "CognitoIdentityServiceProvider." +
                            authData.authData["clientId"] +
                            ".LastAuthUser",
                        authData.authData["username"]
                    );
                    localStorage.setItem(
                        "CognitoIdentityServiceProvider." +
                            authData.authData["clientId"] +
                            "." +
                            authData.authData["username"] +
                            ".idToken",
                        authData.authData["idToken"]
                    );
                    localStorage.setItem(
                        "CognitoIdentityServiceProvider." +
                            authData.authData["clientId"] +
                            "." +
                            authData.authData["username"] +
                            ".accessToken",
                        authData.authData["accessToken"]
                    );
                    localStorage.setItem(
                        "CognitoIdentityServiceProvider." +
                            authData.authData["clientId"] +
                            "." +
                            authData.authData["username"] +
                            ".refreshToken",
                        authData.authData["refreshToken"]
                    );
                    localStorage.setItem(
                        "CognitoIdentityServiceProvider." +
                            authData.authData["clientId"] +
                            "." +
                            authData.authData["username"] +
                            ".clockDrift",
                        authData.authData["clockDrift"]
                    );
                    localStorage.setItem(
                        "InspectionViewOnly",
                        authData.message.limited
                    );
                    localStorage.setItem(
                        "Inspection",
                        authData.message.inspection
                    );

                    ReactGA.event({
                        category: "Login As",
                        action: "success",
                        label: authData.message.inspection,
                        value: 0
                    });

                    setAuthState(AuthState.SignedIn);

                    window.location.href = `/inspection/${authData.message.inspection}`;
                }
            } else {
                const inspectionView = localStorage.getItem("InspectionViewOnly");
                if (!inspectionView) {
                    const clientId = awsconfig.aws_user_pools_web_client_id;
                    const lastAuthUser = Cookies.get(
                        `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
                    );
                    const idToken = Cookies.get(
                        `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`
                    );
                    const refreshToken = Cookies.get(
                        `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.refreshToken`
                    );
                    const clockDrift = Cookies.get(
                        `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.clockDrift`
                    );
                    const userData = Cookies.get(
                        `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.userData`
                    );
                    const accessToken = Cookies.get(
                        `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.accessToken`
                    );

                    if (
                        lastAuthUser &&
                        idToken &&
                        refreshToken &&
                        clockDrift &&
                        userData &&
                        accessToken
                    ) {
                        localStorage.setItem(
                            "CognitoIdentityServiceProvider." +
                                clientId +
                                ".LastAuthUser",
                            lastAuthUser
                        );
                        localStorage.setItem(
                            "CognitoIdentityServiceProvider." +
                                clientId +
                                "." +
                                lastAuthUser +
                                ".idToken",
                            idToken
                        );
                        localStorage.setItem(
                            "CognitoIdentityServiceProvider." +
                                clientId +
                                "." +
                                lastAuthUser +
                                ".accessToken",
                            accessToken
                        );
                        localStorage.setItem(
                            "CognitoIdentityServiceProvider." +
                                clientId +
                                "." +
                                lastAuthUser +
                                ".refreshToken",
                            refreshToken
                        );
                        localStorage.setItem(
                            "CognitoIdentityServiceProvider." +
                                clientId +
                                "." +
                                lastAuthUser +
                                ".clockDrift",
                            clockDrift
                        );
                        localStorage.setItem(
                            `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.userData`,
                            userData
                        );

                        setAuthState(AuthState.SignedIn);

                    } else {
                        if (!token) {
                            localStorage.clear();
                        }
                        window.location.assign(
                            `${Routes.authUrlProtocol}://${process.env.REACT_APP_AUTH_URL}?redirect=${window.location.href}`
                        );
                    }
                }
            }
        }
        setTimeout(() => {
          checkAuth();
        }, 100);

    }, []);

    return (
        <div className={style.holding}>
            <img
                className={style.idwalLogo}
                src={"/images/idwal-logo-reversed.svg"}
                alt={"idwal logo"}
            />
            {valid ? (
                <div className={style.verifying}>
                    Verifying user credentials...
                </div>
            ) : (
                <div className={style.failed}>
                    Sorry, we were unable to use the provided token. Please
                    contact the Idwal Operations team for assistance.
                </div>
            )}
        </div>
    );
}
